import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { auditTime, BehaviorSubject, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { TravelHorseType } from 'src/app/models/enums';
import { GeocodingPlaceFeature } from 'src/app/models/mapbox/place';
import { UserWrapper } from 'src/app/models/user';
import { LocalStorageKeys, GeocodingSearchConstants } from 'src/app/modules/shared/constants';
import { AuthService } from 'src/app/services/auth.service';
import { MapboxService } from 'src/app/services/mapbox.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  
  minDate : Date = new Date();
  private onSourceAddrKeyUpSubject$ = new Subject<KeyboardEvent>();
  private onDestinationAddrKeyUpSubject$ = new Subject<KeyboardEvent>();
  private debounceTimeoutMs : number = GeocodingSearchConstants.debounceTimeoutMs;
  private minSearchTextLength : number = GeocodingSearchConstants.minSearchTextLength;
  private readonly ignoreSearchKeys = GeocodingSearchConstants.ignoreSearchKeys;
  
  filteredSourceOptions$?: Observable<GeocodingPlaceFeature[]>;
  filteredDestinationOptions$?: Observable<GeocodingPlaceFeature[]>;

  sourcePlace?: GeocodingPlaceFeature;
  destinationPlace?: GeocodingPlaceFeature;

  public travelDate : Date = new Date();
  public howItWorksIndex: number = 0;
  public numberOfHorses = 1;
  public TravelHorseType = TravelHorseType;

  public publishYourRideTitle = $localize`Publish your ride`;
  public publishYourRideText = $localize`Share your route or find ride requests`;
  public receiveBookingsTitle = $localize`Receive bookings`;
  public receiveBookingsText = $localize`Get bookings from riders who want to travel with you`;
  public coordinateDetailsTitle = $localize`Negotiate details`;
  public coordinateDetailsText = $localize`Chat with the horse owners directly in the app`;
  public saveMoneyTitle = $localize`Save money`;
  public saveMoneyText = $localize`Share the trip costs with others`;

  public findRideTitle = $localize`Find a ride`;
  public findRideText = $localize`Search for convenient rides or create a ride request`;
  public bookRideTitle = $localize`Book a ride`;
  public bookRideText = $localize`Reserve a spot for your horse in a few clicks`;
  public coordinateDetailsRiderTitle = $localize`Negotiate details`;
  public coordinateDetailsRiderText = $localize`Chat with the drivers directly in the app`;
  public travelTogetherTitle = $localize`Travel together`;
  public travelTogetherText = $localize`Share the trip with other riders and save money`;

  public user$ : BehaviorSubject<UserWrapper>;

  constructor(private router: Router,
    private mapboxService: MapboxService,
    private authService: AuthService) {
    this.user$ = this.authService.user$;
  }
  
  ngOnInit(): void {
    this.filteredSourceOptions$ = this.onSourceAddrKeyUpSubject$.pipe(
      map(event => (event.target as HTMLInputElement)?.value ?? ''), //extract value from event
      tap((value) => {
        if(value.length == 0) {
          this.sourcePlace = undefined;
        }
      }),
      auditTime(this.debounceTimeoutMs),
      switchMap(this.getAddressSuggestions.bind(this))
    );

    this.filteredDestinationOptions$ = this.onDestinationAddrKeyUpSubject$.pipe(
      map(event => (event.target as HTMLInputElement)?.value ?? ''),
      tap((value) => {
        if(value.length == 0) {
          this.destinationPlace = undefined;
        }
      }),
      auditTime(this.debounceTimeoutMs),
      switchMap(this.getAddressSuggestions.bind(this))
    );
  }

  ngOnDestroy(): void {
    this.onSourceAddrKeyUpSubject$.complete();
    this.onDestinationAddrKeyUpSubject$.complete();
  }

  public onHorseNumberChanged(value : number) {
    this.numberOfHorses = value;
  }

  public onSourceAddrTxtKeyUp(event : KeyboardEvent) {
    if(this.ignoreSearchKeys.indexOf(event.key) > -1) {
      return;
    }
    this.onSourceAddrKeyUpSubject$.next(event);
  }

  public onDestinationAddrTxtKeyUp(event : KeyboardEvent) {
    if(this.ignoreSearchKeys.indexOf(event.key) > -1) {
      return;
    }
    this.onDestinationAddrKeyUpSubject$.next(event);
  }

  public addressDisplayFn(mapboxPlace: GeocodingPlaceFeature) : string {
    return mapboxPlace.place_name;
  }

  public onSourceSelected(event : MatAutocompleteSelectedEvent) {
    this.sourcePlace = <GeocodingPlaceFeature>event.option.value;
  }

  public onDestinationSelected(event : MatAutocompleteSelectedEvent) {
    this.destinationPlace = <GeocodingPlaceFeature>event.option.value;
  }

  public navigateToSearchRides() {
    if (this.sourcePlace !== undefined) {
      localStorage.setItem(LocalStorageKeys.sourcePlace, JSON.stringify(this.sourcePlace));
    }
    else {
      localStorage.removeItem(LocalStorageKeys.sourcePlace);
    }

    if (this.destinationPlace !== undefined) {
      localStorage.setItem(LocalStorageKeys.destinationPlace, JSON.stringify(this.destinationPlace));
    }
    else {
      localStorage.removeItem(LocalStorageKeys.destinationPlace);
    }

    if (this.travelDate !== undefined) {
      localStorage.setItem(LocalStorageKeys.travelDate, this.travelDate.toISOString());
    }
    else {
      localStorage.removeItem(LocalStorageKeys.travelDate);
    }
  
    this.router.navigateByUrl('/ride/search');
  }

  public navigateToSearchRequests() {
    if (this.sourcePlace !== undefined) {
      localStorage.setItem(LocalStorageKeys.sourcePlace, JSON.stringify(this.sourcePlace));
    }
    else {
      localStorage.removeItem(LocalStorageKeys.sourcePlace);
    }

    if (this.destinationPlace !== undefined) {
      localStorage.setItem(LocalStorageKeys.destinationPlace, JSON.stringify(this.destinationPlace));
    }
    else {
      localStorage.removeItem(LocalStorageKeys.destinationPlace);
    }

    if (this.travelDate !== undefined) {
      localStorage.setItem(LocalStorageKeys.travelDate, this.travelDate.toISOString());
    }
    else {
      localStorage.removeItem(LocalStorageKeys.travelDate);
    }
  
    this.router.navigateByUrl('/ride-request/search');
  }

  private getAddressSuggestions(value: string) : Observable<GeocodingPlaceFeature[]> {
      if (value.length < this.minSearchTextLength) {
        return of([]);
      }
      return this.mapboxService.getSuggestions(value);
  }
}